'use client';
import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';
import { sendBookingEmails } from '@/lib/email/bookingEmails';
import SessionPaymentForm from './components/SessionPaymentForm';

// ─── Types ────────────────────────────────────────────────────────────────────
interface TimeSlot {
  id: string;
  time: string;
  period: 'AM' | 'PM';
  available: boolean;
}

interface SessionPackage {
  id: string;
  label: string;
  duration: number;
  multiplier: number;
  popular?: boolean;
}

interface Consultant {
  id: string;
  name: string;
  initials: string;
  title: string;
  category: string;
  rating: number;
  reviewCount: number;
  hourlyRate: number;
  availability: 'available' | 'limited' | 'busy';
  verified: boolean;
  responseTime: string;
  completedSessions: number;
  timezone: string;
  languages: string[];
  instantBookingEnabled: boolean;
  slotDurationMinutes: number;
}

// ─── Static Data ──────────────────────────────────────────────────────────────
const sessionPackages: SessionPackage[] = [
  { id: '30', label: '30 min', duration: 30, multiplier: 0.5 },
  { id: '60', label: '1 hour', duration: 60, multiplier: 1, popular: true },
  { id: '90', label: '1.5 hours', duration: 90, multiplier: 1.5 },
  { id: '120', label: '2 hours', duration: 120, multiplier: 2 },
];

// Generate time slots from availability windows
function generateTimeSlotsFromWindows(
  windows: { startTime: string; endTime: string }[],
  slotDurationMinutes: number,
  bookedTimes: string[]
): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const bookedSet = new Set(bookedTimes);

  windows.forEach(w => {
    const [sh, sm] = w.startTime.split(':').map(Number);
    const [eh, em] = w.endTime.split(':').map(Number);
    let cur = sh * 60 + sm;
    const end = eh * 60 + em;
    let idx = 0;
    while (cur + slotDurationMinutes <= end) {
      const hh = String(Math.floor(cur / 60)).padStart(2, '0');
      const mm = String(cur % 60).padStart(2, '0');
      const time = `${hh}:${mm}`;
      const h = Math.floor(cur / 60);
      slots.push({
        id: `slot-${w.startTime}-${idx}`,
        time,
        period: h < 12 ? 'AM' : 'PM',
        available: !bookedSet.has(time),
      });
      cur += slotDurationMinutes;
      idx++;
    }
  });

  return slots;
}

const generateCalendarDays = (advanceDays = 30) => {
  const today = new Date();
  const days = [];
  for (let i = 0; i < advanceDays; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d);
  }
  return days;
};

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const platformFeeRate = 0.05;

// ─── Inner Component (uses useSearchParams) ───────────────────────────────────
function BookSessionInner() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const supabase = createClient();
  const consultantId = searchParams.get('consultant') ?? '';

  const [consultant, setConsultant] = useState<Consultant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [consultantUserId, setConsultantUserId] = useState<string>('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);

  // Availability state
  const [availabilitySlots, setAvailabilitySlots] = useState<{ dayKey: string; startTime: string; endTime: string }[]>([]);
  const [blockedDates, setBlockedDates] = useState<string[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([]);
  const [advanceBookingDays, setAdvanceBookingDays] = useState(30);

  const [calendarDays, setCalendarDays] = useState<Date[]>([]);

  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<SessionPackage>(sessionPackages[1]);
  const [sessionTopic, setSessionTopic] = useState('');
  const [sessionNotes, setSessionNotes] = useState('');
  const [step, setStep] = useState<1 | 2 | 3>(1);

  useEffect(() => {
    async function fetchConsultant() {
      if (!consultantId) {
        setError('No consultant selected. Please browse consultants first.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: fetchError } = await supabase
          .from('consultant_profiles')
          .select(`
            id,
            display_name,
            professional_title,
            category,
            hourly_rate_usd,
            availability_status,
            is_verified,
            languages,
            timezone,
            user_id,
            instant_booking_enabled,
            slot_duration_minutes,
            advance_booking_days
          `)
          .eq('id', consultantId)
          .eq('is_active', true)
          .maybeSingle();

        if (fetchError) {
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        if (!data) {
          setError('Consultant not found.');
          setLoading(false);
          return;
        }

        setConsultantUserId(data.user_id ?? '');

        const advDays = data.advance_booking_days ?? 30;
        setAdvanceBookingDays(advDays);
        const days = generateCalendarDays(advDays);
        setCalendarDays(days);

        // Fetch review stats
        const { data: reviews } = await supabase
          .from('session_reviews')
          .select('rating')
          .eq('reviewee_id', data.user_id);

        const reviewCount = reviews?.length ?? 0;
        const avgRating = reviewCount > 0
          ? (reviews ?? []).reduce((s: number, r: { rating: number }) => s + r.rating, 0) / reviewCount
          : 0;

        // Fetch completed sessions count
        const { count: sessionCount } = await supabase
          .from('consultation_bookings')
          .select('*', { count: 'exact', head: true })
          .eq('provider_user_id', data.user_id)
          .eq('status', 'completed');

        const nameWords = (data.display_name ?? '').split(' ');
        const initials = nameWords.slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase();
        const availability = (['available', 'limited', 'busy'].includes(data.availability_status)
          ? data.availability_status
          : 'available') as 'available' | 'limited' | 'busy';

        setConsultant({
          id: data.id,
          name: data.display_name ?? '',
          initials,
          title: data.professional_title ?? '',
          category: data.category ?? 'General',
          rating: parseFloat(avgRating.toFixed(2)),
          reviewCount,
          hourlyRate: parseFloat(data.hourly_rate_usd ?? 0),
          availability,
          verified: data.is_verified ?? false,
          responseTime: '< 2 hours',
          completedSessions: sessionCount ?? 0,
          timezone: data.timezone ?? 'UTC',
          languages: data.languages ?? [],
          instantBookingEnabled: data.instant_booking_enabled ?? false,
          slotDurationMinutes: data.slot_duration_minutes ?? 60,
        });

        // Fetch availability slots
        const { data: slotData } = await supabase
          .from('consultant_availability_slots')
          .select('day_of_week, start_time, end_time')
          .eq('consultant_profile_id', data.id)
          .eq('is_active', true);

        const mappedAvail = (slotData ?? []).map((s: { day_of_week: string; start_time: string; end_time: string }) => ({
          dayKey: s.day_of_week,
          startTime: (s.start_time ?? '').slice(0, 5),
          endTime: (s.end_time ?? '').slice(0, 5),
        }));
        setAvailabilitySlots(mappedAvail);

        // Fetch blocked dates
        const { data: blockedData } = await supabase
          .from('consultant_blocked_dates')
          .select('blocked_date')
          .eq('consultant_profile_id', data.id);

        setBlockedDates((blockedData ?? []).map((b: { blocked_date: string }) => b.blocked_date));

        // Set default selected day to first available day
        const firstAvailDay = days.find(d => {
          const dk = DAY_KEYS[d.getDay()];
          const dateStr = d.toISOString().split('T')[0];
          const hasSlots = mappedAvail.some((s: { dayKey: string }) => s.dayKey === dk);
          const isBlocked = (blockedData ?? []).some((b: { blocked_date: string }) => b.blocked_date === dateStr);
          return hasSlots && !isBlocked;
        });
        if (firstAvailDay) setSelectedDay(firstAvailDay);
        else if (days.length > 0) setSelectedDay(days[0]);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load consultant');
      } finally {
        setLoading(false);
      }
    }

    fetchConsultant();
  }, [consultantId]);

  // ─── Load time slots when day changes ──────────────────────────────────────
  useEffect(() => {
    if (!selectedDay || !consultant) return;
    setSlotsLoading(true);

    async function loadTimeSlotsForDay() {
      if (!selectedDay || !consultant) return;
      const dayKey = DAY_KEYS[selectedDay.getDay()];
      const dateStr = selectedDay.toISOString().split('T')[0];

      // Get windows for this day
      const windows = availabilitySlots.filter(s => s.dayKey === dayKey);

      if (windows.length === 0) {
        setTimeSlots([]);
        setSlotsLoading(false);
        return;
      }

      // Fetch already-booked slots for this consultant on this date
      const { data: bookedData } = await supabase
        .from('consultation_bookings')
        .select('start_time')
        .eq('provider_user_id', consultantUserId)
        .eq('booking_date', dateStr)
        .in('status', ['pending', 'confirmed']);

      const bookedTimes = (bookedData ?? []).map((b: { start_time: string }) => (b.start_time ?? '').slice(0, 5));
      const generated = generateTimeSlotsFromWindows(windows, consultant.slotDurationMinutes, bookedTimes);
      setTimeSlots(generated);
      setSlotsLoading(false);
    }

    loadTimeSlotsForDay();
  }, [selectedDay, consultant, availabilitySlots, consultantUserId]);

  const sessionCost = (consultant?.hourlyRate ?? 0) * selectedPackage.multiplier;
  const platformFee = sessionCost * platformFeeRate;
  const totalCost = sessionCost + platformFee;

  const formatDate = (d: Date) =>
    `${DAY_NAMES[d.getDay()]}, ${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

  const canProceed = selectedSlot !== null && sessionTopic.trim().length > 0;

  const availabilityColor = consultant ? {
    available: 'text-emerald-600 bg-emerald-50',
    limited: 'text-amber-600 bg-amber-50',
    busy: 'text-red-600 bg-red-50',
  }[consultant.availability] : '';

  const availabilityLabel = consultant ? {
    available: 'Available',
    limited: 'Limited Slots',
    busy: 'Busy',
  }[consultant.availability] : '';

  const isDayAvailable = (day: Date) => {
    const dk = DAY_KEYS[day.getDay()];
    const dateStr = day.toISOString().split('T')[0];
    const hasSlots = availabilitySlots.some(s => s.dayKey === dk);
    const isBlocked = blockedDates.includes(dateStr);
    return hasSlots && !isBlocked;
  };

  const handleProceedToCheckout = async () => {
    if (!consultant || !selectedSlot) return;
    setBookingLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (user && consultantUserId) {
        // Fetch consultant email
        const { data: consultantProfile } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', consultantUserId)
          .maybeSingle();

        const consultantEmail = consultantProfile?.email ?? '';
        const clientEmail = user.email ?? '';

        if (consultantEmail && clientEmail) {
          const sessionDate = selectedDay ? formatDate(selectedDay) : '';
          const sessionTime = `${selectedSlot.time} ${selectedSlot.period}`;
          const durationLabel = selectedPackage.label;
          const sessionCostVal = consultant.hourlyRate * selectedPackage.multiplier;
          const platformFeeVal = sessionCostVal * platformFeeRate;
          const consultantEarnings = parseFloat((sessionCostVal - platformFeeVal).toFixed(2));

          sendBookingEmails({
            consultantEmail,
            clientEmail,
            consultantName: consultant.name,
            clientName: user.email ?? 'Client',
            sessionDate,
            sessionTime,
            duration: durationLabel,
            sessionTopic: sessionTopic,
            sessionNotes: sessionNotes,
            consultantEarnings,
          });
        }
      }
    } catch (err) {
      console.error('[BookSession] Email send error:', err);
    } finally {
      setBookingLoading(false);
      setStep(3);
    }
  };

  const handleCreatePaymentIntent = async (): Promise<{ clientSecret: string; paymentIntentId: string }> => {
    if (!consultant) throw new Error('No consultant selected');
    const { data: { user } } = await supabase.auth.getUser();
    const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;

    // Create a booking record first (or reuse existing)
    let resolvedBookingId = bookingId;
    if (!resolvedBookingId && user) {
      const dateStr = selectedDay ? selectedDay.toISOString().split('T')[0] : '';
      const startTimeStr = selectedSlot?.time ?? '00:00';
      // Calculate end time based on duration
      const [sh, sm] = startTimeStr.split(':').map(Number);
      const endMinutes = sh * 60 + sm + selectedPackage.duration;
      const endTimeStr = `${String(Math.floor(endMinutes / 60)).padStart(2, '0')}:${String(endMinutes % 60).padStart(2, '0')}`;

      const { data: booking } = await supabase
        .from('consultation_bookings')
        .insert({
          client_user_id: user.id,
          provider_user_id: consultantUserId || null,
          consultant_id: consultant.id,
          // Required NOT NULL fields
          service_id: consultant.id,
          service_title: sessionTopic || 'Consultation Session',
          provider_name: consultant.name,
          client_name: user.email ?? 'Client',
          client_email: user.email ?? '',
          // Date/time columns (both canonical and alias)
          booking_date: dateStr,
          start_time: startTimeStr,
          end_time: endTimeStr,
          session_date: dateStr,
          session_time: startTimeStr,
          // Topic/notes (both canonical and alias)
          notes: sessionNotes,
          session_topic: sessionTopic,
          session_notes: sessionNotes,
          duration_minutes: selectedPackage.duration,
          amount_usd: totalCost,
          currency: 'usd',
          status: 'pending',
          payment_status: 'pending',
        })
        .select('id')
        .maybeSingle();
      if (booking?.id) {
        resolvedBookingId = booking.id;
        setBookingId(booking.id);
      }
    }

    const { data: { session } } = await supabase.auth.getSession();
    const accessToken = session?.access_token ?? '';

    const res = await fetch(`${supabaseUrl}/functions/v1/create-consultation-payment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        bookingId: resolvedBookingId,
        consultantId: consultant.id,
        consultantName: consultant.name,
        serviceTitle: sessionTopic,
        amountUsd: totalCost,
        currency: 'usd',
        userId: user?.id,
        email: user?.email,
      }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error((errData as { error?: string }).error ?? 'Failed to create payment intent');
    }

    const data = await res.json() as { clientSecret: string; paymentIntentId: string };
    return { clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId };
  };

  const handlePaymentSuccess = async (paymentIntentId: string) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const accessToken = session?.access_token ?? '';
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;

      await fetch(`${supabaseUrl}/functions/v1/confirm-consultation-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ paymentIntentId, bookingId }),
      });
    } catch (err) {
      console.error('[BookSession] Confirm payment error:', err);
    }
    router.push(`/payment-confirmation?booking=${bookingId ?? ''}&amount=${totalCost.toFixed(2)}`);
  };

  return (
    <AppLayout activeRoute="/book-session">
      <div className="min-h-screen bg-background">
        {/* Header */}
        <div className="border-b border-border bg-card px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            <Link
              href="/browse-consultants"
              className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="ArrowLeftIcon" size={18} />
            </Link>
            <div>
              <h1 className="text-lg font-700 text-foreground">Book a Session</h1>
              <p className="text-sm text-muted-foreground">Select a time slot and confirm your booking</p>
            </div>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
          {/* Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin mb-4" />
              <p className="text-sm text-muted-foreground">Loading consultant details…</p>
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-4">
                <Icon name="ExclamationCircleIcon" size={28} className="text-red-500" />
              </div>
              <h3 className="text-base font-600 text-foreground mb-2">{error}</h3>
              <Link href="/browse-consultants" className="text-sm text-primary font-500 hover:underline">
                Browse Consultants
              </Link>
            </div>
          )}

          {/* Main Content */}
          {!loading && !error && consultant && (
            <>
              {/* Step Indicator */}
              <div className="flex items-center gap-3">
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-colors ${step === 1 ? 'bg-primary text-white' : 'bg-primary/10 text-primary'}`}>
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-700">1</span>
                  Select Time Slot
                </div>
                <div className="flex-1 h-px bg-border" />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-colors ${step === 2 ? 'bg-primary text-white' : step > 2 ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-700 ${step >= 2 ? 'bg-white/20' : 'bg-border'}`}>2</span>
                  Confirm &amp; Review
                </div>
                <div className="flex-1 h-px bg-border" />
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-600 transition-colors ${step === 3 ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'}`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-700 ${step === 3 ? 'bg-white/20' : 'bg-border'}`}>3</span>
                  Payment
                </div>
              </div>

              {step === 1 ? (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left: Consultant Info + Calendar + Slots */}
                  <div className="lg:col-span-2 space-y-5">
                    {/* Consultant Card */}
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-2xl gradient-teal flex items-center justify-center text-white font-700 text-lg flex-shrink-0">
                          {consultant.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h2 className="text-base font-700 text-foreground">{consultant.name}</h2>
                            {consultant.verified && (
                              <span className="flex items-center gap-1 text-xs text-primary font-600">
                                <Icon name="CheckBadgeIcon" size={14} className="text-primary" />
                                Verified
                              </span>
                            )}
                            <span className={`text-xs font-600 px-2 py-0.5 rounded-full ${availabilityColor}`}>
                              {availabilityLabel}
                            </span>
                          </div>
                          <p className="text-sm text-muted-foreground mt-0.5">{consultant.title}</p>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <span className="flex items-center gap-1 text-sm text-foreground font-600">
                              <Icon name="StarIcon" size={14} className="text-amber-400" />
                              {consultant.rating.toFixed(1)}
                              <span className="text-muted-foreground font-400">({consultant.reviewCount})</span>
                            </span>
                            <span className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Icon name="ClockIcon" size={14} />
                              {consultant.responseTime}
                            </span>
                            <span className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Icon name="GlobeAltIcon" size={14} />
                              {consultant.timezone}
                            </span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xl font-700 text-foreground">${consultant.hourlyRate}</p>
                          <p className="text-xs text-muted-foreground">per hour</p>
                        </div>
                      </div>
                    </div>

                    {/* Session Duration */}
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                        <Icon name="ClockIcon" size={16} className="text-primary" />
                        Session Duration
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {sessionPackages.map((pkg) => (
                          <button
                            key={pkg.id}
                            onClick={() => setSelectedPackage(pkg)}
                            className={`relative flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-all duration-150 ${
                              selectedPackage.id === pkg.id
                                ? 'border-primary bg-primary/5 text-primary' : 'border-border hover:border-primary/40 text-foreground'
                            }`}
                          >
                            {pkg.popular && (
                              <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-700 px-2 py-0.5 rounded-full whitespace-nowrap">
                                Popular
                              </span>
                            )}
                            <span className="text-sm font-700">{pkg.label}</span>
                            <span className="text-xs text-muted-foreground">${(consultant.hourlyRate * pkg.multiplier).toFixed(0)}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Calendar */}
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                        <Icon name="CalendarDaysIcon" size={16} className="text-primary" />
                        Select Date
                      </h3>
                      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
                        {calendarDays.map((day, i) => {
                          const isSel = selectedDay ? selectedDay.toDateString() === day.toDateString() : false;
                          const isAvail = isDayAvailable(day);
                          return (
                            <button
                              key={i}
                              onClick={() => { setSelectedDay(day); setSelectedSlot(null); }}
                              className={`flex-shrink-0 flex flex-col items-center gap-1 w-14 py-3 rounded-xl border-2 transition-all duration-150 ${
                                isSel
                                  ? 'border-primary bg-primary text-white'
                                  : !isAvail
                                  ? 'border-border bg-muted/50 text-muted-foreground opacity-40 cursor-not-allowed'
                                  : 'border-border hover:border-primary/40 text-foreground'
                              }`}
                              disabled={!isAvail}
                              title={!isAvail ? 'Not available' : undefined}
                            >
                              <span className="text-[10px] font-600 uppercase tracking-wide">{DAY_NAMES[day.getDay()]}</span>
                              <span className="text-lg font-700">{day.getDate()}</span>
                              <span className="text-[10px]">{MONTH_NAMES[day.getMonth()]}</span>
                              {isAvail && !isSel && <span className="w-1 h-1 rounded-full bg-emerald-500" />}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time Slots */}
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-1 flex items-center gap-2">
                        <Icon name="ClockIcon" size={16} className="text-primary" />
                        Available Times
                        {consultant?.instantBookingEnabled && (
                          <span className="flex items-center gap-1 text-xs text-emerald-600 font-600 bg-emerald-50 px-2 py-0.5 rounded-full">
                            <Icon name="BoltIcon" size={11} />
                            Instant Book
                          </span>
                        )}
                        <span className="ml-auto text-xs text-muted-foreground font-400">{selectedDay ? formatDate(selectedDay) : ''}</span>
                      </h3>
                      <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary/10 border border-primary/30 inline-block" />Available</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-muted border border-border inline-block" />Booked</span>
                        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-primary border border-primary inline-block" />Selected</span>
                      </div>
                      {slotsLoading ? (
                        <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                          <span className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                          <span className="text-sm">Loading available slots…</span>
                        </div>
                      ) : timeSlots.length === 0 ? (
                        <div className="text-center py-8">
                          <Icon name="CalendarIcon" size={28} className="text-muted-foreground mx-auto mb-2" />
                          <p className="text-sm text-muted-foreground">No available slots on this day.</p>
                          <p className="text-xs text-muted-foreground mt-1">Please select another date.</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
                          {timeSlots.map((slot) => (
                            <button
                              key={slot.id}
                              disabled={!slot.available}
                              onClick={() => setSelectedSlot(slot)}
                              className={`py-2 px-1 rounded-xl text-xs font-600 border-2 transition-all duration-150 ${
                                !slot.available
                                  ? 'border-border bg-muted text-muted-foreground/40 cursor-not-allowed line-through'
                                  : selectedSlot?.id === slot.id
                                  ? 'border-primary bg-primary text-white shadow-md'
                                  : 'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:border-primary'
                              }`}
                            >
                              {slot.time}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Session Topic */}
                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                        <Icon name="DocumentTextIcon" size={16} className="text-primary" />
                        Session Details
                      </h3>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
                            Topic / Agenda <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={sessionTopic}
                            onChange={(e) => setSessionTopic(e.target.value)}
                            placeholder="e.g. Sukuk structuring for SME expansion"
                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1.5 block">
                            Additional Notes <span className="text-muted-foreground font-400">(optional)</span>
                          </label>
                          <textarea
                            value={sessionNotes}
                            onChange={(e) => setSessionNotes(e.target.value)}
                            placeholder="Any specific questions or context you'd like the consultant to prepare for..."
                            rows={3}
                            className="w-full px-4 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors resize-none"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: Summary Sidebar */}
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-2xl p-5 sticky top-6">
                      <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                        <Icon name="ReceiptPercentIcon" size={16} className="text-primary" />
                        Booking Summary
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Consultant</span>
                          <span className="font-600 text-foreground text-right">{consultant.name}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Date</span>
                          <span className="font-600 text-foreground text-right text-xs">{selectedDay ? formatDate(selectedDay) : '—'}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Time</span>
                          <span className={`font-600 text-right ${selectedSlot ? 'text-foreground' : 'text-muted-foreground/50'}`}>
                            {selectedSlot ? `${selectedSlot.time} ${selectedSlot.period}` : '—'}
                          </span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="font-600 text-foreground">{selectedPackage.label}</span>
                        </div>

                        <div className="border-t border-border pt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Session fee</span>
                            <span className="font-600 text-foreground">${sessionCost.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Platform fee (5%)</span>
                            <span className="font-600 text-foreground">${platformFee.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm border-t border-border pt-2">
                            <span className="font-700 text-foreground">Total</span>
                            <span className="font-700 text-primary text-base">${totalCost.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <button
                        disabled={!canProceed}
                        onClick={() => setStep(2)}
                        className={`w-full mt-5 py-3 rounded-xl text-sm font-700 transition-all duration-150 flex items-center justify-center gap-2 ${
                          canProceed
                            ? 'bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg'
                            : 'bg-muted text-muted-foreground cursor-not-allowed'
                        }`}
                      >
                        <Icon name="ArrowRightIcon" size={16} />
                        Continue to Review
                      </button>

                      {!selectedSlot && (
                        <p className="text-xs text-muted-foreground text-center mt-2">Select a time slot to continue</p>
                      )}
                      {selectedSlot && !sessionTopic.trim() && (
                        <p className="text-xs text-muted-foreground text-center mt-2">Add a session topic to continue</p>
                      )}
                    </div>

                    {/* Trust Badges */}
                    <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
                      {[
                        { icon: 'ShieldCheckIcon', text: 'Secure payment processing' },
                        { icon: 'ArrowPathIcon', text: 'Free cancellation 24h before' },
                        { icon: 'ChatBubbleLeftEllipsisIcon', text: 'Direct messaging with consultant' },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-xs text-muted-foreground">
                          <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary flex-shrink-0" />
                          {b.text}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : step === 2 ? (
                /* ── Step 2: Confirm & Review ── */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2 space-y-5">
                    <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="CalendarDaysIcon" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-700 text-foreground">Review Your Booking</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Please confirm all details before proceeding to payment.</p>
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                        <Icon name="ClipboardDocumentListIcon" size={16} className="text-primary" />
                        Session Details
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {[
                          { label: 'Consultant', value: consultant.name, icon: 'UserCircleIcon' },
                          { label: 'Expertise', value: consultant.category, icon: 'TagIcon' },
                          { label: 'Date', value: selectedDay ? formatDate(selectedDay) : '—', icon: 'CalendarDaysIcon' },
                          { label: 'Time', value: `${selectedSlot?.time} ${selectedSlot?.period}`, icon: 'ClockIcon' },
                          { label: 'Duration', value: selectedPackage.label, icon: 'ClockIcon' },
                          { label: 'Format', value: 'Video Call', icon: 'VideoCameraIcon' },
                        ].map((item, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-muted/40">
                            <Icon name={item.icon as Parameters<typeof Icon>[0]['name']} size={16} className="text-primary mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-xs text-muted-foreground font-500">{item.label}</p>
                              <p className="text-sm font-600 text-foreground">{item.value}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                        <Icon name="DocumentTextIcon" size={16} className="text-primary" />
                        Your Session Brief
                      </h3>
                      <div className="space-y-3">
                        <div className="p-3 rounded-xl bg-muted/40">
                          <p className="text-xs text-muted-foreground font-500 mb-1">Topic</p>
                          <p className="text-sm text-foreground font-600">{sessionTopic}</p>
                        </div>
                        {sessionNotes && (
                          <div className="p-3 rounded-xl bg-muted/40">
                            <p className="text-xs text-muted-foreground font-500 mb-1">Additional Notes</p>
                            <p className="text-sm text-foreground">{sessionNotes}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl p-5">
                      <h3 className="text-sm font-700 text-foreground mb-3 flex items-center gap-2">
                        <Icon name="UserCircleIcon" size={16} className="text-primary" />
                        About Your Consultant
                      </h3>
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-2xl gradient-teal flex items-center justify-center text-white font-700 flex-shrink-0">
                          {consultant.initials}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-700 text-foreground">{consultant.name}</p>
                          <p className="text-xs text-muted-foreground">{consultant.title}</p>
                          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                            <span className="flex items-center gap-1 text-xs text-foreground font-600">
                              <Icon name="StarIcon" size={12} className="text-amber-400" />
                              {consultant.rating.toFixed(1)} ({consultant.reviewCount} reviews)
                            </span>
                            <span className="text-xs text-muted-foreground">{consultant.completedSessions} sessions</span>
                            <span className="text-xs text-muted-foreground">{consultant.languages.join(', ')}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => setStep(1)}
                      className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Icon name="ArrowLeftIcon" size={16} />
                      Edit booking details
                    </button>
                  </div>

                  {/* Right: Cost Breakdown + Checkout */}
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-2xl p-5 sticky top-6">
                      <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                        <Icon name="CreditCardIcon" size={16} className="text-primary" />
                        Cost Breakdown
                      </h3>

                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">{consultant.name}</span>
                          <span className="font-600 text-foreground">${consultant.hourlyRate}/hr</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="font-600 text-foreground">{selectedPackage.label}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Session fee</span>
                          <span className="font-600 text-foreground">${sessionCost.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Platform fee (5%)</span>
                          <span className="font-600 text-foreground">${platformFee.toFixed(2)}</span>
                        </div>
                        <div className="border-t border-border pt-3 flex justify-between items-center">
                          <span className="font-700 text-foreground text-base">Total Due</span>
                          <span className="font-700 text-primary text-xl">${totalCost.toFixed(2)}</span>
                        </div>
                      </div>

                      <button
                        onClick={handleProceedToCheckout}
                        className="w-full mt-5 py-3 rounded-xl text-sm font-700 bg-primary text-white hover:bg-primary/90 shadow-md hover:shadow-lg transition-all duration-150 flex items-center justify-center gap-2"
                      >
                        <Icon name="CreditCardIcon" size={16} />
                        Proceed to Checkout
                      </button>

                      <p className="text-xs text-muted-foreground text-center mt-3">
                        By proceeding you agree to our{' '}
                        <span className="text-primary cursor-pointer hover:underline">Terms of Service</span>
                      </p>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                      <div className="flex items-start gap-2.5">
                        <Icon name="InformationCircleIcon" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-700 text-amber-800 mb-1">Cancellation Policy</p>
                          <p className="text-xs text-amber-700 leading-relaxed">
                            Free cancellation up to 24 hours before the session. Cancellations within 24 hours are subject to a 50% fee.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ── Step 3: Payment ── */
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <div className="bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-start gap-4 mb-5">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon name="CreditCardIcon" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h2 className="text-base font-700 text-foreground">Complete Payment</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">Enter your card details to confirm your booking with {consultant?.name}.</p>
                      </div>
                    </div>

                    <SessionPaymentForm
                      fees={{
                        consultantRate: consultant?.hourlyRate ?? 0,
                        sessionFee: sessionCost,
                        platformFee: platformFee,
                        totalDue: totalCost,
                        durationLabel: selectedPackage.label,
                        consultantName: consultant?.name ?? '',
                      }}
                      onSuccess={handlePaymentSuccess}
                      onBack={() => setStep(2)}
                      onCreatePaymentIntent={handleCreatePaymentIntent}
                    />
                  </div>

                  {/* Right: Booking recap */}
                  <div className="space-y-4">
                    <div className="bg-card border border-border rounded-2xl p-5 sticky top-6">
                      <h3 className="text-sm font-700 text-foreground mb-4 flex items-center gap-2">
                        <Icon name="CalendarDaysIcon" size={16} className="text-primary" />
                        Booking Recap
                      </h3>
                      <div className="space-y-3 text-sm">
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Consultant</span>
                          <span className="font-600 text-foreground text-right">{consultant?.name}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Date</span>
                          <span className="font-600 text-foreground text-right text-xs">{selectedDay ? formatDate(selectedDay) : '—'}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Duration</span>
                          <span className="font-600 text-foreground">{selectedPackage.label}</span>
                        </div>
                        <div className="flex justify-between items-start gap-2">
                          <span className="text-muted-foreground">Topic</span>
                          <span className="font-600 text-foreground text-right text-xs max-w-[140px] truncate">{sessionTopic}</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
                      {[
                        { icon: 'ShieldCheckIcon', text: 'Secure payment processing' },
                        { icon: 'ArrowPathIcon', text: 'Free cancellation 24h before' },
                        { icon: 'LockClosedIcon', text: 'SSL encrypted transaction' },
                      ].map((b, i) => (
                        <div key={i} className="flex items-center gap-2.5 text-xs text-muted-foreground">
                          <Icon name={b.icon as Parameters<typeof Icon>[0]['name']} size={14} className="text-primary flex-shrink-0" />
                          {b.text}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}

// ─── Page Export with Suspense ────────────────────────────────────────────────
export default function BookSessionPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground text-sm">Loading...</div>
      </div>
    }>
      <BookSessionInner />
    </Suspense>
  );
}
